import { IPaginationResult } from '@models/Equipment.model';
import {
  IEquipmentInventoryApiModel,
  Nullable,
} from '@models/EquipmentInventory.model';
import { isAllNull } from '@utils/isAllNull/isAllNull';
import { sortStr } from '@utils/sorting/non-ascii';
import { nullToUndef } from '../utils/nullToUndef/nullToUndef';
import TagCategory, {
  ITagCategory,
  ITagCategoryApiModel,
  ITagCategoryLimit,
  ITagCategoryLimitWithDesc,
} from './TagCategory.model';

export type MaybeUndef<T> = T | undefined;
export interface ITagRaw {
  id: string;
  name: string;
  color: string;
  tcId: string;
  tcName: string;
}

// All interface members for models are required
export interface ITag {
  id: string;
  name: string;
  tagCategoryId: string;
  tagCategory: MaybeUndef<ITagCategory>;
  description: MaybeUndef<string>;
  createdAt: Date;
  updatedAt: Date;
  lastUsed: Date;
  equipmentsCount: number;
}

/**
 * This is supposed to be an interface compatible with ITag that gives the same info as ITagRaw
 * Note: This only ensures one-way compat from Super to Base
 */
export interface ITagLimit {
  id: string;
  name: string;
  tagCategoryId: string;
  tagCategory: ITagCategoryLimit;
}
export interface ITagLimitWithDesc extends ITagLimit {
  tagCategory: ITagCategoryLimitWithDesc;
  description: ITag['description'];
}
export interface ITagApiModel {
  id: string;
  name: string;
  tagCategoryId: string;
  tagCategory: Nullable<ITagCategoryApiModel>;
  description: Nullable<string>;
  createdAt: string;
  updatedAt: string;
  equipmentsCount: number;
}

export interface ITagApiModel2 {
  id: string;
  name: string;
  tagCategoryId: string;
  description: string | null;
}

export default class Tag implements ITag {
  public readonly id: string;

  public readonly name: string;

  public readonly tagCategoryId: string;

  public readonly tagCategory: MaybeUndef<ITagCategory>;

  public readonly description: MaybeUndef<string>;

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  public readonly lastUsed: Date;

  public readonly equipmentsCount: number;

  constructor(
    id: string,
    name: string,
    tagCategoryId: string,
    equipmentsCount: number,
    createdAt: Date,
    updatedAt: Date,
    lastUsed: Date,
    tagCategory: MaybeUndef<ITagCategory>,
    description: MaybeUndef<string>,
  ) {
    this.id = id;
    this.name = name;
    this.tagCategoryId = tagCategoryId;
    this.equipmentsCount = equipmentsCount;
    this.tagCategory = tagCategory;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.lastUsed = lastUsed;
    this.description = description;
  }

  static fromApiModel(apiModel: ITagApiModel) {
    const {
      id,
      name,
      tagCategoryId,
      description: d,
      equipmentsCount,
    } = apiModel;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));
    const lastUsed = new Date(Date.parse(apiModel.updatedAt));
    const tagCategory = apiModel.tagCategory
      ? TagCategory.fromApiModel(apiModel.tagCategory)
      : undefined;

    return new Tag(
      id,
      name,
      tagCategoryId,
      equipmentsCount,
      createdAt,
      updatedAt,
      lastUsed,
      tagCategory,
      // Todo: Investigate use of description to make sure we don't rely on "null"
      nullToUndef(d),
    );
  }

  static arrayFromApiModel(arrayApiModel: ITagApiModel[]) {
    const _array = arrayApiModel.map(apiModel => Tag.fromApiModel(apiModel));
    const array = Array.from(_array).sort((a, b) => sortStr(a.name, b.name));
    return array;
  }

  static arrayPaginatedFromApiModel(
    arrayApiModel: IPaginationResult<ITagApiModel>,
  ) {
    const _array = arrayApiModel.results.map(apiModel =>
      Tag.fromApiModel(apiModel),
    );
    const array = Array.from(_array).sort((a, b) => sortStr(a.name, b.name));
    return {
      ...arrayApiModel,
      results: array,
    };
  }

  // Todo:  Convert this to use ITagLimit as standalone converter
  static rawArrayFromEquipmentApiModel(
    model: IEquipmentInventoryApiModel,
  ): ITagLimitWithDesc[] {
    const __array = model.tags.filter(v => !isAllNull(v));
    const _array = __array.map(e => {
      const { tagCategoryId, description, ...rest } = e;
      const category = model.tagCategory.find(e2 => e2.id === e.tagCategoryId);
      if (!category) {
        throw new Error(
          "Api invariant failed! All tag's categories must be in tagCategory array",
        );
      }
      return {
        ...rest,
        tagCategoryId,
        description: nullToUndef(description),
        tagCategory: {
          id: tagCategoryId,
          color: category.color,
          name: category.name,
          description: nullToUndef(category.description),
        },
      };
    });
    const array = Array.from(_array).sort((a, b) => sortStr(a.name, b.name));
    return array;
  }
}
